import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { CREATE_WHITEBOARD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import type { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { useIsAllowedToCreateWhiteboard } from '@confluence/content-types-utils/entry-points/useIsAllowedToCreateWhiteboard.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { PaywallStatus } from '../../createContentInputTypes.ts';

import type { useWhiteboardCreationPaywallWhiteboardCreationPaywallContentToDisableQuery as useWhiteboardCreationPaywallWhiteboardCreationPaywallContentToDisableQuery$data } from './__types__/useWhiteboardCreationPaywallWhiteboardCreationPaywallContentToDisableQuery.ts';
type UseWhiteboardCreationPaywallResponse = {
	deactivationIdentifier?: string;
	paywallStatus?: PaywallStatus;
	error?: ApolloErrorInterop;
	showPaywallBeforeCreate: boolean;
};

export const useWhiteboardCreationPaywall = (
	forceSkipQuery = false,
): UseWhiteboardCreationPaywallResponse => {
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const isAllowedToCreateWhiteboard = useIsAllowedToCreateWhiteboard();

	const shouldSkipQuery = forceSkipQuery || !isAllowedToCreateWhiteboard || isPremium;
	const { data, error } =
		useQueryInterop<useWhiteboardCreationPaywallWhiteboardCreationPaywallContentToDisableQuery$data>(
			graphql`
				query useWhiteboardCreationPaywallWhiteboardCreationPaywallContentToDisableQuery {
					paywallContentToDisable(contentType: "whiteboard") {
						deactivationIdentifier
						link
						title
					}
				}
			`,
			// "cache-and-network" reduces caching issues resulting in the paywall modal showing when it shouldn't
			{
				fetchPolicy: 'no-cache',
				skip: shouldSkipQuery,
			},
		);
	if (isPremium) {
		return {
			deactivationIdentifier: undefined,
			paywallStatus: PaywallStatus.ACTIVE,
			showPaywallBeforeCreate: false,
		};
	}

	const deactivationIdentifier = data?.paywallContentToDisable?.deactivationIdentifier;
	return {
		deactivationIdentifier,
		error,
		paywallStatus: data ? PaywallStatus.ACTIVE : PaywallStatus.UNSET,
		showPaywallBeforeCreate: Boolean(deactivationIdentifier),
	};
};

type CollectExperienceParams = React.ComponentProps<typeof ExperienceStart>['collect'];

export const collectStartCreateWhiteboardExperience: CollectExperienceParams = (
	events,
	experience,
) => {
	const whiteboardExperienceStart = events.find(
		(event) => event.action === 'taskStart' && event.name === CREATE_WHITEBOARD_EXPERIENCE,
	);
	if (whiteboardExperienceStart) {
		experience.succeed();
	}
};
